import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Path, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export const REQUIRED_LOAD_FIELDS = [
  ["reference", "Load Number#"], ["shipper_name", "Shipper Name"], ["shipper_address", "Shipper Address"], ["pickup_date", "Pickup Date"],
  ["receiver_name", "Receiver Name"], ["receiver_address", "Receiver Address"], ["delivery_date", "Delivery Date"], ["rate", "Rate"],
  ["loaded_miles", "Loaded Miles"], ["equipment", "Equipment"],
];
export const missingLoadFields = (l) => REQUIRED_LOAD_FIELDS.filter(([k]) => !String(l?.[k] ?? "").trim() || (["rate", "loaded_miles"].includes(k) && !(Number(l[k]) > 0)));

export function AiCompletionPanel({ load, setLoad }) {
  const [truckAt, setTruckAt] = useState(load.truck_location || "");
  const [busy, setBusy] = useState(false);
  const missing = missingLoadFields(load);

  const calc = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/route-miles", { truck_location: truckAt, shipper_address: load.shipper_address || "", receiver_address: load.receiver_address || "" });
      const rate = Number(load.rate) || 0;
      setLoad({ ...load, truck_location: truckAt, loaded_miles: data.loaded_miles, dead_head_miles: data.dead_head_miles, total_miles: data.total_miles,
        rpm: data.total_miles && rate ? +(rate / data.total_miles).toFixed(2) : load.rpm, map: data.route_map });
      toast.success(`Route: ${data.dead_head_miles} DH + ${data.loaded_miles} loaded = ${data.total_miles} mi`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
      <div className={`rounded-xl border p-4 ${missing.length ? "border-amber-300 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="ai-missing-panel">
        {missing.length ? (
          <>
            <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><WarningCircle size={16} weight="fill" />Asterism AI couldn't find {missing.length} field{missing.length > 1 ? "s" : ""}. Please fill {missing.length > 1 ? "them" : "it"} in:</p>
            <div className="flex flex-wrap gap-1.5 mt-2">{missing.map(([k, l]) => <span key={k} className="text-xs font-semibold px-2 py-0.5 rounded bg-white border border-amber-300 text-amber-800" data-testid={`ai-missing-${k}`}>{l}</span>)}</div>
          </>
        ) : <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5"><CheckCircle size={16} weight="fill" />All required load details are complete.</p>}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-miles-panel">
        <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-2"><Path size={16} className="text-blue-600" />Auto-Calculate Miles (US road routing)</p>
        <div className="flex gap-2">
          <Input value={truckAt} onChange={(e) => setTruckAt(e.target.value)} placeholder="Optional: truck start, e.g. Fort Worth, TX" className="bg-white border-slate-300" data-testid="ai-truck-location" />
          <Button onClick={calc} disabled={busy || !load.shipper_address || !load.receiver_address} className="bg-blue-600 text-white hover:bg-blue-700 shrink-0" data-testid="ai-calc-miles">{busy ? "Calculating…" : "Calculate"}</Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5">Loaded = shipper → receiver. Dead Head is auto-calculated from the driver's previous delivery when you assign the driver.</p>
      </div>
    </div>
  );
}
